import React, { useEffect, useState } from "react";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";

import CircularProgress from "components/circular-progress";
import type {
  ConsultDebrief,
  ConsultOutcome,
  ConsultSignalType
} from "services/types/consult-debriefs.type";

import {
  CONSULT_OUTCOME_LABELS,
  SIGNAL_TONES,
  formatTranscriptTimestamp
} from "./consult-debrief.util";

const signalToneSx = (theme: Theme, type: ConsultSignalType) => {
  const paletteColor = theme.palette[SIGNAL_TONES[type].palette];
  return {
    backgroundColor: alpha(paletteColor.main, 0.16),
    color: paletteColor.dark,
    fontWeight: 600,
    fontSize: "0.72rem"
  };
};

const signalDotSx = (theme: Theme, type: ConsultSignalType) => {
  const paletteColor = theme.palette[SIGNAL_TONES[type].palette];
  return {
    width: 8,
    height: 8,
    mt: "7px",
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: paletteColor.main,
    boxShadow: `0 0 6px ${alpha(paletteColor.main, 0.55)}`
  };
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      sx={{
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: "0.08em",
        lineHeight: 1.4
      }}
    >
      {children}
    </Typography>
  );
}

export type ConsultDebriefContentProps = {
  debrief: ConsultDebrief | undefined;
  isLoading: boolean;
  isError: boolean;
  isConfirming: boolean;
  confirmFailed: boolean;
  onConfirmOutcome: (outcome: ConsultOutcome) => void;
};

/**
 * Presentational body of a consult debrief — summary, consult-arc phases, signals,
 * follow-ups, and the outcome picker/confirm action. No data fetching: the two callers
 * (`ConsultDebriefDialog`, `DebriefTab`) each own their own query/mutation and pass the
 * result down, so this component has no opinion on where it's rendered (Dialog body or an
 * inline tab panel).
 */
export default function ConsultDebriefContent({
  debrief,
  isLoading,
  isError,
  isConfirming,
  confirmFailed,
  onConfirmOutcome
}: ConsultDebriefContentProps) {
  const [selectedOutcome, setSelectedOutcome] = useState<ConsultOutcome | "">(
    ""
  );

  useEffect(() => {
    setSelectedOutcome(debrief?.outcome ?? "");
  }, [debrief?.outcome]);

  const outcomeConfirmed = debrief?.outcome_source === "clinician_confirmed";
  // S18a (PZ review): the backend's confirm-outcome endpoint has no status filter on its
  // queryset, so a PENDING (or FAILED) debrief is confirmable server-side — the FE must be
  // the one gate preventing a clinician from locking in a guessed outcome before the AI has
  // even produced one. Only a complete debrief can be confirmed.
  const isComplete = debrief?.status === "complete";
  const canConfirm =
    isComplete &&
    Boolean(selectedOutcome) &&
    !isConfirming &&
    (selectedOutcome !== debrief?.outcome || !outcomeConfirmed);

  const phases = debrief?.payload?.phases ?? [];

  return (
    <>
      {isLoading && <CircularProgress sx={{ width: "100%", my: 2 }} />}
      {isError && (
        <Alert severity="error">Could not load the consult debrief.</Alert>
      )}
      {debrief?.status === "pending" && (
        <Alert severity="info">
          This consult is still being analyzed. Check back in a few minutes.
        </Alert>
      )}
      {debrief?.status === "failed" && (
        <Alert severity="error">
          The debrief for this consult could not be generated.
        </Alert>
      )}
      {debrief && isComplete && (
        <Stack gap={2.25}>
          {debrief.payload?.summaries?.overall && (
            <Box
              sx={(theme) => ({
                backgroundColor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                borderRadius: 1.5,
                px: 2,
                py: 1.5
              })}
            >
              <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                {debrief.payload.summaries.overall}
              </Typography>
            </Box>
          )}

          {phases.length > 0 && (
            <Box>
              <SectionLabel>Consult Arc</SectionLabel>
              <Stack direction="row" gap={0.75} sx={{ mt: 0.75 }}>
                {phases.map((phase) => (
                  <Box key={phase.phase} sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={(theme) => ({
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.55)
                      })}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        fontWeight: 600,
                        textTransform: "capitalize",
                        color: "text.primary"
                      }}
                    >
                      {phase.phase.replace(/_/g, " ")}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", lineHeight: 1.45 }}
                    >
                      {phase.summary}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <SectionLabel>Signals</SectionLabel>
            {debrief.signals.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No signals captured for this consult.
              </Typography>
            )}
            <Stack
              gap={1}
              sx={(theme) => ({
                mt: 0.75,
                backgroundColor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1.5,
                px: 2,
                py: 1.5
              })}
            >
              {debrief.signals.map((signal) => {
                const timestamp = formatTranscriptTimestamp(
                  signal.transcript_ts
                );
                return (
                  <Stack
                    key={signal.id}
                    direction="row"
                    alignItems="flex-start"
                    gap={1.25}
                  >
                    <Box sx={(theme) => signalDotSx(theme, signal.type)} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {signal.summary}
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={0.75}
                        sx={{ mt: 0.25 }}
                      >
                        <Chip
                          size="small"
                          label={
                            SIGNAL_TONES[signal.type]?.label ?? signal.type
                          }
                          sx={(theme) => signalToneSx(theme, signal.type)}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontVariantNumeric: "tabular-nums"
                          }}
                        >
                          {signal.subtype.replace(/_/g, " ")}
                          {timestamp ? ` · ${timestamp}` : ""}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          {Boolean(debrief.payload?.follow_ups?.length) && (
            <Box>
              <SectionLabel>Suggested Follow-ups</SectionLabel>
              <Stack gap={0.75} sx={{ mt: 0.75 }}>
                {debrief.payload?.follow_ups?.map((followUp, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    gap={1}
                  >
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 17, color: "text.secondary" }}
                    />
                    <Typography variant="body2">
                      {followUp.description}
                      {followUp.due_hint && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: "text.secondary", ml: 0.75 }}
                        >
                          · {followUp.due_hint}
                        </Typography>
                      )}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <SectionLabel>Outcome</SectionLabel>
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", mb: 0.75 }}
            >
              {outcomeConfirmed
                ? "Confirmed by a clinician."
                : debrief.outcome_source === "inferred"
                  ? "Inferred from same-day scheduling — confirm or correct it."
                  : "Select what happened and confirm."}
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {Object.entries(CONSULT_OUTCOME_LABELS).map(([value, label]) => {
                const selected = selectedOutcome === value;
                return (
                  <Chip
                    key={value}
                    label={label}
                    clickable
                    onClick={() => setSelectedOutcome(value as ConsultOutcome)}
                    sx={(theme) => ({
                      fontWeight: 600,
                      ...(selected
                        ? {
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            "&:hover": {
                              backgroundColor: theme.palette.primary.dark
                            }
                          }
                        : {
                            backgroundColor: "background.paper",
                            border: `1px solid ${theme.palette.divider}`,
                            color: "text.primary"
                          })
                    })}
                  />
                );
              })}
            </Stack>
            {confirmFailed && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.75 }}
              >
                Could not confirm the outcome. Please try again.
              </Typography>
            )}
          </Box>
        </Stack>
      )}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          disabled={!canConfirm}
          startIcon={<TaskAltIcon />}
          onClick={() => selectedOutcome && onConfirmOutcome(selectedOutcome)}
        >
          {isConfirming
            ? "Confirming…"
            : outcomeConfirmed && selectedOutcome === debrief?.outcome
              ? "Outcome Confirmed"
              : "Confirm Outcome"}
        </Button>
      </Box>
    </>
  );
}
